"use client";

import type { ToolUIRegistry } from "@magnus/react";
import { lazy } from "react";

/**
 * Excel client-side tool UI registry.
 *
 * All Excel tools share the same status component (`ExcelToolStatusView`),
 * except `batch_operations` which uses `ExcelBatchToolStatusView`.
 */

const excelStatusView = () =>
  import("@/components/chat/excel-tool-status").then(
    ({ ExcelToolStatusView }) => ({ default: ExcelToolStatusView })
  );

const excelBatchView = () =>
  import("@/components/chat/excel-tool-status").then(
    ({ ExcelBatchToolStatusView }) => ({ default: ExcelBatchToolStatusView })
  );

const step = (loader: typeof excelStatusView) => ({
  kind: "step" as const,
  category: "code" as const,
  view: lazy(loader),
});

export const excelToolRegistry = {
  // Inspection
  get_sheet_names: step(excelStatusView),
  get_active_sheet_info: step(excelStatusView),
  read_formatting: step(excelStatusView),
  render_range_image: step(excelStatusView),
  // Data I/O
  read_range: step(excelStatusView),
  read_range_to_file: step(excelStatusView),
  write_range: step(excelStatusView),
  write_range_from_file: step(excelStatusView),
  set_cell_formula: step(excelStatusView),
  clear_range: step(excelStatusView),
  // Formatting
  format_range: step(excelStatusView),
  set_borders: step(excelStatusView),
  auto_fit: step(excelStatusView),
  set_dimensions: step(excelStatusView),
  merge_cells: step(excelStatusView),
  // Sheet Management
  create_sheet: step(excelStatusView),
  delete_sheet: step(excelStatusView),
  rename_sheet: step(excelStatusView),
  // Tables & Charts
  create_table: step(excelStatusView),
  create_chart: step(excelStatusView),
  // Data Operations
  sort_range: step(excelStatusView),
  auto_filter: step(excelStatusView),
  find_replace: step(excelStatusView),
  // Structure
  insert_rows_columns: step(excelStatusView),
  delete_rows_columns: step(excelStatusView),
  // Advanced
  set_conditional_formatting: step(excelStatusView),
  add_data_validation: step(excelStatusView),
  set_page_layout: step(excelStatusView),
  // Comments
  add_comment: step(excelStatusView),
  get_comments: step(excelStatusView),
  delete_comment: step(excelStatusView),
  // Batch
  batch_operations: step(excelBatchView),
} satisfies ToolUIRegistry;
